// store/forms/jsaForm/autosaveErrors/jsaFormAutosaveErrorsActions.js

import { jsaAutosaveErrorsInitialState } from "../jsaFormState";

/**
 * Creates autosave error actions for the JSA form.
 * These actions manage validation and database error state from WebSocket autosave operations.
 *
 * JSA can be embedded in DEL or Sampling Event forms, but maintains its own error state
 * within jsaFormUI.autosaveErrors for clean separation of concerns.
 *
 * @param {Object} helpers - Object containing setJsaUi helper function
 * @returns {Object} Frozen object containing error management actions
 */
export const makeJsaAutosaveErrorsActions = (helpers) => ({
  /**
   * Set a field-level error from validation failure
   * @param {string} patchType - The patch type (jsa_basic_info, jsa_rac_info, etc.)
   * @param {string} fieldName - The field that failed validation
   * @param {string} errorMessage - User-friendly error message
   */
  setFieldError(patchType, fieldName, errorMessage) {
    const key = `${patchType}.${fieldName}`;
    helpers.setJsaUi((prev) => ({
      ...prev,
      autosaveErrors: {
        ...prev.autosaveErrors,
        hasErrors: true,
        lastError: {
          key,
          message: errorMessage,
          timestamp: Date.now(),
          patchType,
        },
        fieldErrors: {
          ...prev.autosaveErrors.fieldErrors,
          [key]: errorMessage,
        },
      },
    }));
  },

  /**
   * Set row-level errors for row-based sections (signatures, training employees, etc.)
   * @param {string} rowId - UUID of the row with errors
   * @param {Object} errors - Object mapping field names to error messages
   * @param {string} patchType - The patch type for context
   */
  setRowError(rowId, errors, patchType) {
    helpers.setJsaUi((prev) => ({
      ...prev,
      autosaveErrors: {
        ...prev.autosaveErrors,
        hasErrors: true,
        lastError: {
          key: rowId,
          message: Object.values(errors)[0] || "Validation error",
          timestamp: Date.now(),
          patchType,
          rowId,
        },
        rowErrors: {
          ...prev.autosaveErrors.rowErrors,
          [rowId]: errors,
        },
      },
    }));
  },

  /**
   * Clear error for a specific field (called on successful save)
   * @param {string} patchType - The patch type
   * @param {string} fieldName - The field to clear
   */
  clearFieldError(patchType, fieldName) {
    const key = `${patchType}.${fieldName}`;
    helpers.setJsaUi((prev) => {
      const currentErrors = prev.autosaveErrors || jsaAutosaveErrorsInitialState;
      const newFieldErrors = { ...currentErrors.fieldErrors };
      delete newFieldErrors[key];
      const hasRemainingErrors =
        Object.keys(newFieldErrors).length > 0 ||
        Object.keys(currentErrors.rowErrors).length > 0;
      return {
        ...prev,
        autosaveErrors: {
          ...currentErrors,
          fieldErrors: newFieldErrors,
          hasErrors: hasRemainingErrors,
          lastError: hasRemainingErrors ? currentErrors.lastError : null,
        },
      };
    });
  },

  /**
   * Clear errors for a specific row
   * @param {string} rowId - UUID of the row to clear
   */
  clearRowError(rowId) {
    helpers.setJsaUi((prev) => {
      const currentErrors = prev.autosaveErrors || jsaAutosaveErrorsInitialState;
      const newRowErrors = { ...currentErrors.rowErrors };
      delete newRowErrors[rowId];
      const hasRemainingErrors =
        Object.keys(currentErrors.fieldErrors).length > 0 ||
        Object.keys(newRowErrors).length > 0;
      return {
        ...prev,
        autosaveErrors: {
          ...currentErrors,
          rowErrors: newRowErrors,
          hasErrors: hasRemainingErrors,
          lastError: hasRemainingErrors ? currentErrors.lastError : null,
        },
      };
    });
  },

  /**
   * Clear all errors for a specific patch type
   * @param {string} patchType - The patch type to clear (jsa_basic_info, jsa_signatures, etc.)
   */
  clearPatchTypeErrors(patchType) {
    helpers.setJsaUi((prev) => {
      const currentErrors = prev.autosaveErrors || jsaAutosaveErrorsInitialState;
      const newFieldErrors = Object.fromEntries(
        Object.entries(currentErrors.fieldErrors).filter(
          ([key]) => !key.startsWith(`${patchType}.`)
        )
      );
      const hasRemainingErrors =
        Object.keys(newFieldErrors).length > 0 ||
        Object.keys(currentErrors.rowErrors).length > 0;
      return {
        ...prev,
        autosaveErrors: {
          ...currentErrors,
          fieldErrors: newFieldErrors,
          hasErrors: hasRemainingErrors,
          lastError: hasRemainingErrors ? currentErrors.lastError : null,
        },
      };
    });
  },

  /**
   * Reset all autosave errors (called on form load/navigation)
   */
  resetAutosaveErrors() {
    helpers.setJsaUi((prev) => ({
      ...prev,
      autosaveErrors: jsaAutosaveErrorsInitialState,
    }));
  },

  /**
   * Process server error response and set appropriate errors
   * This is the main entry point for handling structured errors from the backend.
   * @param {Object} errorData - Structured error from server
   */
  handleServerError(errorData) {
    if (!errorData) return;

    const { error_type, patch_type, field_errors, row_id, summary } = errorData;

    if (error_type === "validation" && field_errors) {
      helpers.setJsaUi((prev) => {
        const currentErrors = prev.autosaveErrors || jsaAutosaveErrorsInitialState;

        // Build new field errors
        const newFieldErrors = { ...currentErrors.fieldErrors };
        Object.entries(field_errors).forEach(([field, message]) => {
          const key = `${patch_type}.${field}`;
          newFieldErrors[key] = message;
        });

        // Build new row errors if row_id present
        const newRowErrors = { ...currentErrors.rowErrors };
        if (row_id && field_errors) {
          newRowErrors[row_id] = field_errors;
        }

        // Get first field error for lastError message
        const firstFieldKey = Object.keys(field_errors)[0];
        const firstFieldMessage = field_errors[firstFieldKey];

        return {
          ...prev,
          autosaveErrors: {
            hasErrors: true,
            lastError: {
              key: row_id || `${patch_type}.${firstFieldKey}`,
              message: firstFieldMessage,
              timestamp: Date.now(),
              patchType: patch_type,
              rowId: row_id,
              summary,
            },
            fieldErrors: newFieldErrors,
            rowErrors: newRowErrors,
          },
        };
      });
    } else if (error_type === "database") {
      // Database errors don't have field-level detail
      helpers.setJsaUi((prev) => ({
        ...prev,
        autosaveErrors: {
          ...prev.autosaveErrors,
          hasErrors: true,
          lastError: {
            key: "database",
            message: summary || "Database error",
            timestamp: Date.now(),
            patchType: patch_type,
            rowId: row_id,
            summary,
          },
        },
      }));
    }
  },
});
